import React, { useState } from "react";
import { ArrowRight, Settings, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, trDate, openPdf, formatApiError } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import CashAccountsDialog, { TYPES } from "@/components/cashbook/CashAccountsDialog";
import { clearAccountsCache } from "@/components/AccountSelect";

export default function CashBalanceStrip({ summary, onChanged, period, date }) {
  const [open, setOpen] = useState(false);
  const s = summary;
  const accounts = s.accounts || [];
  const openStatement = async (a) => {
    try { const r = await api.get(`/cashbook/accounts/${a.id}/export`, { params: { period, date }, responseType: "blob" }); openPdf(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Ekstre oluşturulamadı"); }
  };
  return (
    <Card className="p-4 sm:p-5" data-testid="cashbook-balance-strip">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-xs text-muted-foreground">{s.initial_date ? `Açılış ${trDate(s.initial_date)} · Toplam açılış ${TRY(s.initial)}` : "Açılış tarihi tanımlı değil"} · {accounts.length} hesap</div>
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)} data-testid="cashbook-accounts-btn" className="gap-1.5 text-muted-foreground shrink-0"><Settings className="h-4 w-4" /> Hesaplar</Button>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {TYPES.map((t) => {
          const list = accounts.filter((a) => a.type === t.key);
          const tot = s.by_type?.[t.key] || { opening: 0, income: 0, expense: 0, closing: 0, transfer: 0 };
          return (
            <div key={t.key} className="rounded-xl border border-border/60 bg-muted/20 p-3 sm:p-4" data-testid={`${t.key}-strip`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2.5">
                  <div className={`h-9 w-9 rounded-lg ${t.bg} ${t.tone} flex items-center justify-center`}><t.icon className="h-4 w-4" /></div>
                  <div><div className="text-sm font-semibold">{t.label}</div><div className="text-[11px] text-muted-foreground">{list.length} hesap</div></div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Toplam</div>
                  <div className={`font-mono text-lg font-bold ${tot.closing >= 0 ? "text-primary" : "text-rose-400"}`} data-testid={`${t.key}-closing`}>{TRY(tot.closing)}</div>
                </div>
              </div>
              <div className="space-y-2">
                {list.length === 0 && <div className="text-xs text-muted-foreground">Hesap yok — "Hesaplar"dan açın</div>}
                {list.map((a) => (
                  <div key={a.id} className="rounded-lg bg-card/60 border border-border/40 px-3 py-2" data-testid={`account-strip-${a.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">{a.name}{!a.active && <span className="text-[10px] uppercase text-muted-foreground">pasif</span>}
                        <button onClick={() => openStatement(a)} title="Hesap ekstresi (PDF)" data-testid={`account-statement-${a.id}`} className="h-5 w-5 rounded hover:bg-accent inline-flex items-center justify-center text-muted-foreground hover:text-primary"><FileText className="h-3 w-3" /></button>
                      </div>
                      <div className={`font-mono font-bold ${a.closing >= 0 ? "" : "text-rose-400"}`} data-testid={`account-closing-${a.id}`}>{TRY(a.closing)}</div>
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground flex-wrap">
                      <span title="Devreden">{TRY(a.opening)}</span><ArrowRight className="h-3 w-3" />
                      <span className="text-emerald-400" title="Gelir">+{TRY(a.income)}</span>
                      <span className="text-rose-400" title="Gider">−{TRY(a.expense)}</span>
                      {a.transfer !== 0 && <span className="text-sky-400" title="Virman">{a.transfer > 0 ? "+" : ""}{TRY(a.transfer)} virman</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-end gap-2 text-sm" data-testid="cashbook-closing">
        <span className="text-muted-foreground">Toplam Dönem Sonu (tüm hesaplar):</span>
        <span className={`font-mono text-xl font-bold ${s.closing >= 0 ? "text-primary" : "text-rose-400"}`}>{TRY(s.closing)}</span>
      </div>
      <CashAccountsDialog open={open} onClose={() => setOpen(false)} onChanged={() => { clearAccountsCache(); onChanged(); }} />
    </Card>
  );
}
